import { encode, NetworkType, useFormatAmount, useFormatter, useIntl } from '@capsicum/common';
import { type WalletFanToken } from '@capsicum/features/wallet/shared';
import { useMemo } from 'react';

import { useLedgerSummaryQuery } from '../data-access/queries';
import { CURRENCY_LABELS, CurrencyType, TypeCurrency, type WalletWithdrawFanTokens } from '../models';

import { useWalletCurrencies } from './useWalletCurrencies';
import { useWalletFanTokens } from './useWalletFanTokens';
import { useWalletWithdrawalMinimumAmount } from './useWalletWithdrawalMinimumAmount';
import { useWithdrawRemainingLimit } from './useWithdrawRemainingLimit';

export interface WithdrawDetailsChilizInfo {
	amount: string;
	hasEnoughCHZAvailabletoWithdraw: boolean;
	hasEnoughCHZtoWithdraw: boolean;
	logoAmount: string;
	logoLabel: string;
	minAmount: string;
	subTitle: string;
	title: string;
	tooltipText: string;
}
export interface WithdrawDetailsFanTokensInfo {
	amount: string;
	hasEnoughFantokensToWithdraw: boolean | undefined;
	logoLabel: string;
	minAmount: string;
	subTitle: string;
	title: string;
}

/**
 *
 * @returns all the info for main withdraw page
 */

export const useWalletWithdrawDetails = () => {
	const { $t } = useIntl();
	const { formatAmount } = useFormatAmount();
	const { data: tokens, isLoading: tokensLoading } = useWalletFanTokens();
	const { data: currencies, isLoading: currenciesLoading } = useWalletCurrencies();
	const { isLoading: minCHZLoading, minAmount: minCHZ } = useWalletWithdrawalMinimumAmount(TypeCurrency.Crypto);
	const { isLoading: minFTLoading, minAmount: minFT } = useWalletWithdrawalMinimumAmount(TypeCurrency.VoteToken);
	const { cryptoLimit, fiatLimit } = useWithdrawRemainingLimit(currencies);
	const { formatCrypto, formatFiat } = useFormatter();
	const { data: tokenSummary, isLoading: summaryLoading } = useLedgerSummaryQuery();

	const withdrawalTexts = {
		available: $t('wallet.withdraw.available'),
		fanTokensExtraText: $t('wallet.withdraw.fanTokensTeams'),
		fanTokensLogoText: $t('wallet.withdraw.fanTokensLogoText'),
		minWithdrawalText: $t('wallet.withdraw.minWithdrawal'),
		notEnoughFanTokens: $t('wallet.withdraw.notEnoughFanTokens'),
		tooltip: $t('wallet.withdraw.tooltip')
	};

	// Add fav tokens and map tradable amounts from summary call
	const fanTokens = tokens && [...tokens.fanTokens, tokens.favouriteFanToken];
	const formattedFanTokenData: Array<WalletFanToken> | undefined = fanTokens?.reduce(
		(a: Array<WalletFanToken>, p) => {
			const mappedTokensById = tokenSummary?.find(u => u?.ledger?.id === p?.ledger?.id);

			const isExternalToken = mappedTokensById?.ledger?.currency?.type === TypeCurrency.ExternalToken;

			if (mappedTokensById)
				a?.push({
					...p,
					tradableBalance: isExternalToken
						? Number(mappedTokensById.tradableBalance)?.toFixed(2)?.toString()
						: mappedTokensById?.tradableBalance
				});

			return a;
		},
		[]
	);

	const withdrawFanTokens: Array<WalletWithdrawFanTokens> | undefined = useMemo(
		() =>
			formattedFanTokenData?.map(fanToken => ({
				amount: Number(fanToken?.tradableBalance),
				enabled: fanToken?.tradableBalance
					? Number(fanToken?.tradableBalance) >= Number(formatAmount(minFT))
					: false,
				formattedAmount: `${fanToken?.tradableBalance || 0} ${fanToken.ledger?.currency?.shortCode}`,
				id: fanToken?.ledger?.currency?.id,
				isCC2Enabled: fanToken?.ledger?.currencyNetwork?.network?.type === NetworkType.CC2,
				isExternalToken: fanToken?.ledger?.currency?.type === TypeCurrency.ExternalToken,
				minAmount: minFT,
				partnerName: fanToken?.partner?.name,
				shortCode: fanToken?.ledger?.currency?.shortCode,
				smartContractAddress: fanToken?.ledger?.currency?.smartContractAddress,
				url: fanToken?.partner?.logo
			})),
		[formatAmount, minFT, formattedFanTokenData]
	);

	const formattedMinChilizAmountText = `${formatAmount(minCHZ)} ${currencies?.defaultCurrency?.shortCode}`;
	const formattedMaxChilizAmountText = formatCrypto(cryptoLimit?.remainingAmountBeforeLimit || 0, 'CHZ');
	const formatedConversionAmount = formatFiat(
		fiatLimit?.remainingAmountBeforeLimit || 0,
		currencies?.altCurrency?.shortCode
	);
	const hasEnoughCHZAvailabletoWithdraw = Number(cryptoLimit?.remainingAmountBeforeLimit || 0) > Number(minCHZ);
	const hasEnoughCHZtoWithdraw =
		Number(cryptoLimit?.remainingAmountBeforeLimit || 0) + Number(currencies?.defaultCurrency?.rawLockedBalance) >
		Number(formatAmount(minCHZ));
	const hasEnoughFantokensToWithdraw = withdrawFanTokens?.some(x => x.enabled);
	// needed to show exact number +extra near the logos
	const hasExtraNonVisibleTokens = withdrawFanTokens ? withdrawFanTokens.length - 5 : 0;

	const chilizInfo: WithdrawDetailsChilizInfo = {
		amount: formattedMinChilizAmountText,
		hasEnoughCHZAvailabletoWithdraw,
		hasEnoughCHZtoWithdraw,
		logoAmount: `${formattedMaxChilizAmountText} (${encode('doubleTilde')}${formatedConversionAmount})`,
		logoLabel: withdrawalTexts.available,
		minAmount: formatAmount(minCHZ),
		subTitle: `${withdrawalTexts.minWithdrawalText}`,
		title: CURRENCY_LABELS[CurrencyType.CHILIZ],
		tooltipText: withdrawalTexts.tooltip
	};
	const fanTokensInfo: WithdrawDetailsFanTokensInfo = {
		amount: withdrawFanTokens
			? `${formatAmount(minFT)} ${CURRENCY_LABELS[CurrencyType.FANTOKENS]} ${withdrawalTexts.fanTokensExtraText}`
			: '',
		hasEnoughFantokensToWithdraw,
		logoLabel:
			hasExtraNonVisibleTokens > 0 ? `${hasExtraNonVisibleTokens}${withdrawalTexts.fanTokensLogoText}` : '',
		minAmount: formatAmount(minFT),
		subTitle: withdrawFanTokens ? withdrawalTexts.minWithdrawalText : withdrawalTexts.notEnoughFanTokens,
		title: CURRENCY_LABELS[CurrencyType.FANTOKENS]
	};

	const isLoading = minCHZLoading || minFTLoading || currenciesLoading || tokensLoading || summaryLoading;

	return { chilizInfo, fanTokensInfo, isLoading, withdrawFanTokens };
};
